<template>
  <div class="emp-schedule-page">
    <!-- 앱 헤더 -->
    <div class="app-header">
      <div class="header-title">일정계획</div>
    </div>

    <div class="page-content">
    <!-- 월 선택 -->
    <div class="month-selector">
      <button class="nav-btn" @click="changeMonth(-1)">
        <v-icon size="20">mdi-chevron-left</v-icon>
      </button>
      <div class="current-month" @click="goToCurrentMonth">
        <span class="year">{{ selectedYear }}년</span>
        <span class="month">{{ selectedMonth }}월</span>
      </div>
      <button class="nav-btn" @click="changeMonth(1)">
        <v-icon size="20">mdi-chevron-right</v-icon>
      </button>
    </div>

    <!-- 캘린더 -->
    <div class="calendar-card">
      <div class="calendar-header">
        <span v-for="day in weekDays" :key="day" class="day-name">{{ day }}</span>
      </div>
      <div class="calendar-body">
        <div 
          v-for="(day, index) in calendarDays" 
          :key="index"
          class="calendar-day"
          :class="{
            'other-month': !day.isCurrentMonth,
            'today': day.isToday,
            'has-work': day.hasWork,
            'weekend': day.isWeekend
          }"
          @click="showDayDetail(day)"
        >
          <span class="day-number">{{ day.day }}</span>
          <div v-if="day.hasWork" class="work-indicator">
            <span class="work-count" v-if="day.workCount > 1">{{ day.workCount }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 이번 달 근무 요약 -->
    <div class="summary-card">
      <h3>
        <v-icon size="20" color="#059669">mdi-chart-bar</v-icon>
        {{ selectedMonth }}월 근무 요약
      </h3>
      <div v-if="isLoading" class="loading-summary">
        <v-progress-circular indeterminate size="24" color="#059669" />
      </div>
      <div v-else class="summary-grid">
        <div class="summary-item">
          <span class="value">{{ workSummary.workDays }}</span>
          <span class="label">근무일</span>
        </div>
        <div class="summary-item">
          <span class="value">{{ formatHours(workSummary.totalMinutes) }}</span>
          <span class="label">총 시간</span>
        </div>
        <div class="summary-item">
          <span class="value">{{ workSummary.serviceCount }}</span>
          <span class="label">서비스 건</span>
        </div>
      </div>
    </div>

    <!-- 근무 상세 내역 -->
    <div class="schedule-list-section" v-if="scheduleData.length > 0">
      <h3 class="section-title">
        <v-icon size="20" color="#059669">mdi-calendar-text</v-icon>
        {{ selectedMonth }}월 근무 상세
      </h3>
      <div class="schedule-list">
        <div 
          v-for="(item, index) in groupedSchedule" 
          :key="index"
          class="schedule-day-group"
        >
          <div class="day-header">
            <span class="day-date">{{ formatDateShort(item.date) }}</span>
            <span class="day-label">{{ getDayOfWeek(item.date) }}</span>
            <span class="day-count">{{ item.services.length }}건</span>
          </div>
          <div class="services-list">
            <div 
              v-for="(service, sIdx) in item.services" 
              :key="sIdx"
              class="service-item"
            >
              <div class="service-time">
                <v-icon size="14" color="#9ca3af">mdi-clock-outline</v-icon>
                <span>{{ service.leb_start_time }} ~ {{ service.leb_end_time }}</span>
              </div>
              <div class="service-info">
                <span class="patient-name">{{ service.leb_patient_name }}</span>
                <span class="suga-name">{{ service.leb_suga_name || service.leb_service_gubun }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 데이터 없음 -->
    <div v-else-if="!isLoading" class="empty-state">
      <v-icon size="48" color="#d1d5db">mdi-calendar-blank</v-icon>
      <p>{{ selectedMonth }}월 근무 내역이 없습니다.</p>
    </div>

    <!-- 일자별 상세 다이얼로그 -->
    <v-dialog v-model="showDetailDialog" max-width="360">
      <v-card class="detail-dialog-card" v-if="selectedDay">
        <div class="dialog-header">
          <h2>{{ formatDateFull(selectedDay.fullDate) }}</h2>
          <button class="close-btn" @click="showDetailDialog = false">
            <v-icon size="20">mdi-close</v-icon>
          </button>
        </div>
        <div class="dialog-content" v-if="selectedDay.services && selectedDay.services.length > 0">
          <div 
            v-for="(service, idx) in selectedDay.services" 
            :key="idx"
            class="detail-service-item"
          >
            <div class="service-header">
              <span class="time">{{ service.leb_start_time }} ~ {{ service.leb_end_time }}</span>
              <span class="duration">{{ calculateDuration(service.leb_start_time, service.leb_end_time) }}</span>
            </div>
            <div class="service-body">
              <div class="info-row">
                <span class="label">수급자</span>
                <span class="value">{{ service.leb_patient_name }}</span>
              </div>
              <div class="info-row" v-if="service.leb_suga_name">
                <span class="label">서비스</span>
                <span class="value">{{ service.leb_suga_name }}</span>
              </div>
            </div>
          </div>
        </div>
        <div class="dialog-content empty" v-else>
          <v-icon size="40" color="#d1d5db">mdi-calendar-blank</v-icon>
          <p>이 날에는 근무 내역이 없습니다.</p>
        </div>
      </v-card>
    </v-dialog>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import axios from 'axios';

const store = useStore();

// 사원 정보
const pwaUser = computed(() => store.getters['pwa/pwaUser']);
const empName = computed(() => pwaUser.value?.emp_name || '');
const empBirth = computed(() => pwaUser.value?.emp_birth || '');
const sisulCode = computed(() => pwaUser.value?.m_sisul_code || '');

// 현재 날짜
const today = new Date();
const selectedYear = ref(today.getFullYear());
const selectedMonth = ref(today.getMonth() + 1);

// 데이터
const isLoading = ref(false);
const scheduleData = ref<any[]>([]);
const workSummary = ref({
  workDays: 0,
  serviceCount: 0,
  totalMinutes: 0
});

// 다이얼로그
const showDetailDialog = ref(false);
const selectedDay = ref<any>(null);

// 요일 이름
const weekDays = ['일', '월', '화', '수', '목', '금', '토'];

// 월 변경
const changeMonth = (delta: number) => {
  let newMonth = selectedMonth.value + delta;
  let newYear = selectedYear.value;
  
  if (newMonth > 12) {
    newMonth = 1;
    newYear++;
  } else if (newMonth < 1) {
    newMonth = 12;
    newYear--;
  }
  
  selectedMonth.value = newMonth;
  selectedYear.value = newYear;
};

// 현재 월로 이동
const goToCurrentMonth = () => {
  selectedYear.value = today.getFullYear();
  selectedMonth.value = today.getMonth() + 1;
};

// 년월 문자열 생성
const yearMonth = computed(() => {
  return `${selectedYear.value}${String(selectedMonth.value).padStart(2, '0')}`;
});

// 근무 일자 집합 (빠른 조회용)
const workDatesSet = computed(() => {
  const dates = new Set<string>();
  scheduleData.value.forEach(item => {
    if (item.leb_date) {
      dates.add(item.leb_date);
    }
  });
  return dates;
});

// 일자별 근무 횟수
const workCountByDate = computed(() => {
  const counts: Record<string, number> = {};
  scheduleData.value.forEach(item => {
    if (item.leb_date) {
      counts[item.leb_date] = (counts[item.leb_date] || 0) + 1;
    }
  });
  return counts;
});

// 일자별 서비스 목록
const servicesByDate = computed(() => {
  const map: Record<string, any[]> = {};
  scheduleData.value.forEach(item => {
    if (item.leb_date) {
      if (!map[item.leb_date]) {
        map[item.leb_date] = [];
      }
      map[item.leb_date].push(item);
    }
  });
  return map;
});

// 그룹화된 스케줄 (일자별)
const groupedSchedule = computed(() => {
  const dates = Object.keys(servicesByDate.value).sort();
  return dates.map(date => ({
    date,
    services: servicesByDate.value[date]
  }));
});

// 캘린더 일수 계산
const calendarDays = computed(() => {
  const year = selectedYear.value;
  const month = selectedMonth.value;
  
  // 해당 월의 첫 날과 마지막 날
  const firstDay = new Date(year, month - 1, 1);
  const lastDay = new Date(year, month, 0);
  
  // 첫 주의 시작 (일요일부터)
  const startDay = new Date(firstDay);
  startDay.setDate(startDay.getDate() - firstDay.getDay());
  
  const days = [];
  const current = new Date(startDay);
  
  // 6주 표시 (42일)
  for (let i = 0; i < 42; i++) {
    const isCurrentMonth = current.getMonth() === month - 1;
    const isToday = 
      current.getFullYear() === today.getFullYear() &&
      current.getMonth() === today.getMonth() &&
      current.getDate() === today.getDate();
    const isWeekend = current.getDay() === 0 || current.getDay() === 6;
    
    // 날짜 문자열 생성 (YYYY-MM-DD)
    const dateStr = `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, '0')}-${String(current.getDate()).padStart(2, '0')}`;
    const hasWork = isCurrentMonth && workDatesSet.value.has(dateStr);
    const workCount = workCountByDate.value[dateStr] || 0;
    
    days.push({
      day: current.getDate(),
      fullDate: dateStr,
      isCurrentMonth,
      isToday,
      isWeekend,
      hasWork,
      workCount,
      services: servicesByDate.value[dateStr] || []
    });
    
    current.setDate(current.getDate() + 1);
  }
  
  return days;
});

// 시간 포맷 (HHMM -> HH:mm)
const formatTimeToDisplay = (time: string): string => {
  if (!time) return '';
  const str = time.toString().replace(/[^0-9]/g, '');
  if (str.length >= 4) {
    return `${str.slice(0, 2)}:${str.slice(2, 4)}`;
  }
  return time;
};

// 날짜 포맷 (YYYYMMDD -> YYYY-MM-DD)
const formatDateToStandard = (date: string): string => {
  if (!date) return '';
  // 이미 YYYY-MM-DD 형식이면 그대로 반환
  if (date.includes('-')) return date;
  const str = date.toString().replace(/[^0-9]/g, '');
  if (str.length === 8) {
    return `${str.slice(0, 4)}-${str.slice(4, 6)}-${str.slice(6, 8)}`;
  }
  return date;
};

// 데이터 조회 (일정계획 테이블 사용 - les_)
const fetchScheduleData = async () => {
  if (!empName.value || !sisulCode.value) {
    console.warn('사원 정보가 없습니다.');
    return;
  }

  isLoading.value = true;
  try {
    const encodedName = encodeURIComponent(empName.value);
    const birth = empBirth.value || 'null';
    
    // 일정계획과 요약 동시 조회 (schedule-plan API 사용)
    const [scheduleRes, summaryRes] = await Promise.all([
      axios.get(`/api/emp/schedule-plan/${encodedName}/${birth}/${sisulCode.value}/${yearMonth.value}`, {
        withCredentials: true
      }),
      axios.get(`/api/emp/schedule-plan-summary/${encodedName}/${birth}/${sisulCode.value}/${yearMonth.value}`, {
        withCredentials: true
      })
    ]);

    if (scheduleRes.data.success) {
      // les_ 필드를 leb_ 필드 형식으로 변환하여 기존 템플릿 호환성 유지
      scheduleData.value = (scheduleRes.data.data || []).map((item: any) => ({
        leb_date: formatDateToStandard(item.les_date),
        leb_start_time: formatTimeToDisplay(item.les_start_time),
        leb_end_time: formatTimeToDisplay(item.les_end_time),
        leb_patient_name: item.les_patient_name,
        leb_suga_name: item.les_suga_name,
        leb_service_gubun: item.les_service_gubun
      }));
    } else {
      scheduleData.value = [];
    }

    if (summaryRes.data.success) {
      const data = summaryRes.data.data;
      workSummary.value = {
        workDays: data.work_days || 0,
        serviceCount: data.service_count || 0,
        totalMinutes: data.total_minutes || 0
      };
    } else {
      workSummary.value = { workDays: 0, serviceCount: 0, totalMinutes: 0 };
    }
  } catch (error) {
    console.error('일정계획 조회 오류:', error);
    scheduleData.value = [];
    workSummary.value = { workDays: 0, serviceCount: 0, totalMinutes: 0 };
  } finally {
    isLoading.value = false;
  }
};

// 시간 포맷 (분 -> 시간)
const formatHours = (minutes: number): string => {
  if (!minutes) return '0h';
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
};

// 날짜 포맷 (짧은 형식)
const formatDateShort = (dateStr: string): string => {
  if (!dateStr) return '';
  const parts = dateStr.split('-');
  if (parts.length === 3) {
    return `${parseInt(parts[1])}/${parseInt(parts[2])}`;
  }
  return dateStr;
};

// 날짜 포맷 (전체)
const formatDateFull = (dateStr: string): string => {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('ko-KR', {
      month: 'long',
      day: 'numeric',
      weekday: 'long'
    });
  } catch {
    return dateStr;
  }
};

// 요일 구하기
const getDayOfWeek = (dateStr: string): string => {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    return weekDays[d.getDay()];
  } catch {
    return '';
  }
};

// 시간 차이 계산
const calculateDuration = (start: string, end: string): string => {
  if (!start || !end) return '';
  try {
    const [sh, sm] = start.split(':').map(Number);
    const [eh, em] = end.split(':').map(Number);
    const startMinutes = sh * 60 + sm;
    const endMinutes = eh * 60 + em;
    const diff = endMinutes - startMinutes;
    if (diff <= 0) return '';
    const hours = Math.floor(diff / 60);
    const mins = diff % 60;
    if (hours === 0) return `${mins}분`;
    if (mins === 0) return `${hours}시간`;
    return `${hours}시간 ${mins}분`;
  } catch {
    return '';
  }
};

// 일자 상세 보기
const showDayDetail = (day: any) => {
  if (!day.isCurrentMonth) return;
  selectedDay.value = day;
  showDetailDialog.value = true;
};

// 월 변경 시 데이터 재조회
watch([selectedYear, selectedMonth], () => {
  fetchScheduleData();
});

onMounted(() => {
  fetchScheduleData();
});
</script>

<style scoped lang="scss">
.emp-schedule-page {
  min-height: 100vh;
  background: #f5f5f5;
}

.app-header {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 14px 16px;
  background: linear-gradient(135deg, #059669 0%, #10b981 100%);
  color: white;
  position: sticky;
  top: 0;
  z-index: 100;
  
  .header-title {
    font-size: 17px;
    font-weight: 600;
  }
}

.page-content {
  padding: 16px;
  padding-bottom: 80px;
}

.page-header {
  margin-bottom: 20px;
  
  h1 {
    font-size: 22px;
    font-weight: 700;
    color: #1f2937;
    margin: 0 0 4px 0;
  }
  
  p {
    font-size: 14px;
    color: #6b7280;
    margin: 0;
  }
}

.month-selector {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  margin-bottom: 16px;
}

.nav-btn {
  width: 36px;
  height: 36px;
  border: none;
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:active {
    transform: scale(0.95);
    background: #f3f4f6;
  }
}

.current-month {
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  
  .year {
    font-size: 13px;
    color: #6b7280;
  }
  
  .month {
    font-size: 24px;
    font-weight: 700;
    color: #059669;
  }
}

.calendar-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  padding: 16px;
  margin-bottom: 16px;
}

.calendar-header {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
  margin-bottom: 8px;
}

.day-name {
  text-align: center;
  font-size: 12px;
  font-weight: 600;
  color: #9ca3af;
  padding: 8px 0;
  
  &:first-child {
    color: #dc2626;
  }
  
  &:last-child {
    color: #2563eb;
  }
}

.calendar-body {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
}

.calendar-day {
  aspect-ratio: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  position: relative;
  transition: all 0.2s ease;
  cursor: pointer;
  
  .day-number {
    font-size: 14px;
    font-weight: 500;
    color: #374151;
  }
  
  &.other-month {
    cursor: default;
    .day-number {
      color: #d1d5db;
    }
  }
  
  &.weekend:not(.other-month) {
    &:nth-child(7n+1) .day-number {
      color: #dc2626;
    }
    &:nth-child(7n) .day-number {
      color: #2563eb;
    }
  }
  
  &.today {
    background: #059669;
    
    .day-number {
      color: white;
      font-weight: 700;
    }
  }
  
  &.has-work:not(.today) {
    background: #dcfce7;
  }
  
  .work-indicator {
    position: absolute;
    bottom: 4px;
    width: auto;
    min-width: 6px;
    height: 6px;
    padding: 0 2px;
    background: #059669;
    border-radius: 3px;
    display: flex;
    align-items: center;
    justify-content: center;
    
    .work-count {
      font-size: 8px;
      font-weight: 700;
      color: white;
    }
  }
  
  &.today .work-indicator {
    background: white;
    
    .work-count {
      color: #059669;
    }
  }
}

.summary-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  padding: 20px;
  margin-bottom: 16px;
  
  h3 {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 16px;
    font-weight: 700;
    color: #1f2937;
    margin: 0 0 16px 0;
  }
}

.loading-summary {
  display: flex;
  justify-content: center;
  padding: 20px;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

.summary-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 12px;
  background: #f0fdf4;
  border-radius: 12px;
  
  .value {
    font-size: 22px;
    font-weight: 700;
    color: #059669;
  }
  
  .label {
    font-size: 12px;
    color: #6b7280;
    margin-top: 4px;
  }
}

// 근무 상세 내역
.schedule-list-section {
  margin-bottom: 16px;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 12px 0;
}

.schedule-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.schedule-day-group {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}

.day-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: #f0fdf4;
  border-bottom: 1px solid #dcfce7;
  
  .day-date {
    font-size: 15px;
    font-weight: 700;
    color: #059669;
  }
  
  .day-label {
    font-size: 13px;
    color: #6b7280;
  }
  
  .day-count {
    margin-left: auto;
    font-size: 12px;
    font-weight: 600;
    color: white;
    background: #059669;
    padding: 2px 8px;
    border-radius: 10px;
  }
}

.services-list {
  padding: 8px;
}

.service-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 10px 12px;
  border-radius: 8px;
  transition: background-color 0.2s ease;
  
  &:not(:last-child) {
    border-bottom: 1px solid #f3f4f6;
  }
}

.service-time {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #6b7280;
}

.service-info {
  display: flex;
  align-items: center;
  gap: 8px;
  
  .patient-name {
    font-size: 14px;
    font-weight: 600;
    color: #1f2937;
  }
  
  .suga-name {
    font-size: 12px;
    color: #9ca3af;
    background: #f3f4f6;
    padding: 2px 6px;
    border-radius: 4px;
  }
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  text-align: center;
  
  p {
    margin-top: 12px;
    font-size: 15px;
    color: #6b7280;
  }
}

// 상세 다이얼로그
.detail-dialog-card {
  border-radius: 16px !important;
  overflow: hidden;
}

.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
  
  h2 {
    font-size: 16px;
    font-weight: 700;
    color: white;
    margin: 0;
  }
  
  .close-btn {
    width: 28px;
    height: 28px;
    border: none;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 6px;
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
  }
}

.dialog-content {
  padding: 16px;
  max-height: 60vh;
  overflow-y: auto;
  
  &.empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 40px 20px;
    
    p {
      margin-top: 12px;
      font-size: 14px;
      color: #6b7280;
    }
  }
}

.detail-service-item {
  padding: 14px;
  background: #f9fafb;
  border-radius: 12px;
  margin-bottom: 12px;
  
  &:last-child {
    margin-bottom: 0;
  }
}

.service-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  
  .time {
    font-size: 15px;
    font-weight: 700;
    color: #059669;
  }
  
  .duration {
    font-size: 12px;
    color: #6b7280;
    background: white;
    padding: 4px 8px;
    border-radius: 6px;
  }
}

.service-body {
  .info-row {
    display: flex;
    justify-content: space-between;
    padding: 6px 0;
    
    .label {
      font-size: 13px;
      color: #9ca3af;
    }
    
    .value {
      font-size: 13px;
      font-weight: 600;
      color: #374151;
    }
  }
}
</style>
